import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    OnInit,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { startWith } from 'rxjs';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import {
    FieldInterventionCreateEditModalData,
    FieldInterventionCreationEditionForm,
    FieldInterventionModalCloseModeEnum,
} from '@connect-services/client/models/create-edit-field-intervention-modal.model';
import { MultiPolygon } from '@connect-services/client/sdk/api';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { CustomDateAdapterService, DayOfWeekEnum } from '@connect-services/client/services/custom-date-adapter.service';
import {
    ADAPTER_DATE_FORMATS,
    EN_DATE_FORMAT,
    TranslateService,
} from '@connect-services/client/services/translate.service';
import { ValidatorsService } from '@connect-services/client/services/validators.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { FormService } from '@shared/client/services/form.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ComplexModalTemplate, RessourcesModule],
    providers: [
        { deps: [TranslateService], provide: DateAdapter, useClass: CustomDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: ADAPTER_DATE_FORMATS },
    ],
    selector: 'app-new-field-intervention-modal',
    templateUrl: './create-edit-field-intervention-modal.component.html',
})
export class CreateEditFieldInterventionModalComponent implements AfterViewInit, OnInit {
    public readonly nameInput = viewChild.required<ElementRef<HTMLInputElement>>('nameInput');

    protected readonly confirmTooltip = computed<string | undefined>(() => {
        if (this.hasFieldInterventionArea()) {
            return undefined;
        }

        return this.translocoService.translate('defineInterventionArea');
    });
    protected dateFormatPlaceHolder = EN_DATE_FORMAT;
    protected readonly hasFieldInterventionArea = signal<boolean>(false);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly customDateAdapterService = inject(CustomDateAdapterService);
    protected readonly data: FieldInterventionCreateEditModalData = inject(MAT_DIALOG_DATA);
    protected readonly dialogRef = inject(MatDialogRef<CreateEditFieldInterventionModalComponent>);
    protected fieldInterventionCreationEditionForm!: FieldInterventionCreationEditionForm;
    protected readonly formService = inject(FormService);

    private contentLayoutService = inject(ContentLayoutService);
    private dateAdapter = inject(DateAdapter<Date>);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translateService = inject(TranslateService);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.dateFormatPlaceHolder = this.translateService.getDateFormat();
        this.dateAdapter.getFirstDayOfWeek = (): number => DayOfWeekEnum.MONDAY;
    }

    public ngAfterViewInit(): void {
        this.nameInput().nativeElement.focus();
    }

    public ngOnInit(): void {
        this.initForm();
        this.initFieldInterventionAreaState();
    }

    public onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }
        this.dialogRef.close({
            creationEditionForm: this.fieldInterventionCreationEditionForm,
            mode: FieldInterventionModalCloseModeEnum.SAVE,
        });
    }

    public onDefineInterventionArea(): void {
        this.contentLayoutService.isDataTableView.set(false);

        this.dialogRef.close({
            creationEditionForm: this.fieldInterventionCreationEditionForm,
            mode: FieldInterventionModalCloseModeEnum.FIELD_INTERVENTION_AREA_SELECTION,
        });
    }

    private initFieldInterventionAreaState(): void {
        const fieldInterventionAreaControl = this.fieldInterventionCreationEditionForm.controls.fieldInterventionArea;

        fieldInterventionAreaControl.valueChanges
            .pipe(startWith(fieldInterventionAreaControl.value), takeUntilDestroyed(this.destroyRef))
            .subscribe((fieldInterventionArea: MultiPolygon | null) => {
                this.hasFieldInterventionArea.set(!!fieldInterventionArea);
            });
    }

    private initForm(): void {
        this.fieldInterventionCreationEditionForm = new FieldInterventionCreationEditionForm({
            description: new FormControl<string | null>(this.data.description ?? null, {
                nonNullable: false,
                validators: [ValidatorsService.maxLength(FormService.VARCHAR_COMMON_LENGTH)],
            }),
            fieldInterventionArea: new FormControl<MultiPolygon | null>(this.data.fieldInterventionArea ?? null, {
                nonNullable: false,
                validators: [Validators.required],
            }),
            name: new FormControl<string>(this.data.name ?? '', {
                nonNullable: true,
                validators: [
                    ...ValidatorsService.stringValidationForm({
                        maxLength: FormService.VARCHAR_MEDIUM_LENGTH,
                        minLength: FormService.VARCHAR_MIN_LENGTH,
                    }),
                    ValidatorsService.forbiddenCharsValidator(FormService.FORBIDDEN_CHARS),
                ],
            }),
            scheduledEndDate: new FormControl<Date | null>(this.data.scheduledEndDate ?? null, {
                validators: ValidatorsService.validateDateAfterNow(),
            }),
        });
    }
}
